import React, { useState } from "react";
import { Bed, ChevronDown, ChevronRight, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CotCatalogSearch } from "@/pages/cotizador/CotCatalogSearch";
import { CotSalesDescription, salesFromPlantilla } from "@/pages/cotizador/CotSalesDescription";
import {
  ROOM_TYPES, ROOM_PAX_DEFAULT, BOARD_TYPES, fmtEUR, nightsBetween, accommodationTotals, newRoom,
} from "@/pages/cotizador/shared";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Ficha de un alojamiento. Misma fuente de datos en el sumario y en el día de check-in:
// editar en cualquiera de los dos sitios actualiza el mismo objeto (sincronizado).
export function CotAccommodationCard({ a, roomConfig, numTravelers, markup, onChange, onRemove, tid, collapsible = false, defaultOpen = false, headerLabel }) {
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);

  const buildDefaultRooms = () => {
    const cfg = (roomConfig && roomConfig.length) ? roomConfig : [{ room_type: "doble", pax: 2, quantity: 1 }];
    return cfg.flatMap((rc) => Array.from({ length: rc.quantity || 1 }, () => newRoom({ room_type: rc.room_type, pax: rc.pax })));
  };
  const recompute = (x) => {
    if ((x.rooms || []).length > 0) { const t = accommodationTotals(x); return { ...x, cost_tax_incl: t.incl, cost_tax_excl: t.excl }; }
    return x;
  };
  const upd = (patch) => onChange(recompute({ ...a, ...patch }));
  const pickHotel = async (p) => {
    const existing = (a.rooms && a.rooms.length) ? a.rooms : buildDefaultRooms();
    const incl = Number(p.cost_tax_incl) || Number(p.cost_tax_excl) || 0;
    const excl = Number(p.cost_tax_excl) || incl;
    const rooms = existing.map((r) => ({ ...r, cost_incl: incl, cost_excl: excl }));
    const patch = { ...a, name: p.name, provider_id: p.provider_id || null, product_id: p.id, city: p.city || "", country: p.country || "", currency: p.currency || "EUR", rooms };
    // It.3 · Autorrellena la descripción de venta desde la plantilla TP vinculada al hotel.
    if (p.tp_plantilla_id && !a.plantilla_id && !a.sales_description) {
      try { Object.assign(patch, await salesFromPlantilla(p.tp_plantilla_id)); } catch { /* ignora */ }
    }
    onChange(recompute(patch));
  };
  const addRoom = () => upd({ rooms: [...(a.rooms || []), newRoom()] });
  const removeRoom = (rid) => upd({ rooms: (a.rooms || []).filter((r) => r.id !== rid) });
  const patchRoom = (rid, patch) => {
    const rooms = (a.rooms || []).map((r) => {
      if (r.id !== rid) return r;
      const m = { ...r, ...patch };
      if ("room_type" in patch && !("pax" in patch)) m.pax = ROOM_PAX_DEFAULT[patch.room_type] || m.pax;
      return m;
    });
    upd({ rooms });
  };

  const t = accommodationTotals(a);
  const incl = (a.rooms || []).length ? t.incl : (Number(a.cost_tax_incl) || 0);
  const pvp = incl * (1 + (Number(markup) || 0) / 100);
  const roomsPax = (a.rooms || []).reduce((s, r) => s + (r.pax || 0), 0);
  const nights = nightsBetween(a.date_from, a.date_to);
  const perStay = a.price_mode === "per_stay";
  const usingRooms = (a.rooms || []).length > 0;

  const body = (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Hotel / Apartamento</div>
          <CotCatalogSearch value={a.name} productType="alojamiento" testid={`${tid}-name`} placeholder="Buscar hotel del catálogo…"
            onTextChange={(v) => upd({ name: v })} onPick={pickHotel} />
        </div>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Desde</span>
          <Input type="date" value={a.date_from || ""} onChange={(e) => upd({ date_from: e.target.value })} className="h-7 w-36 text-xs" data-testid={`${tid}-from`} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Hasta</span>
          <Input type="date" value={a.date_to || ""} onChange={(e) => upd({ date_to: e.target.value })} className="h-7 w-36 text-xs" data-testid={`${tid}-to`} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Régimen</span>
          <select value={a.board || "bb"} onChange={(e) => upd({ board: e.target.value })} className="h-7 rounded border bg-background px-1.5 text-xs outline-none focus:ring-1 focus:ring-ring" data-testid={`${tid}-board`}>
            {BOARD_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select></label>
        <label className="flex flex-col gap-0.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">Total c/IVA</span>
          <input type="number" min="0" step="0.01" value={incl} readOnly={usingRooms} title={usingRooms ? "Calculado desde las habitaciones" : "Editable"}
            onChange={(e) => upd({ cost_tax_incl: parseFloat(e.target.value || "0"), cost_tax_excl: parseFloat(e.target.value || "0") })}
            className={`h-7 w-28 rounded border bg-background px-1.5 text-right text-xs tabular-nums outline-none ${usingRooms ? "text-muted-foreground" : "focus:ring-1 focus:ring-ring"}`} data-testid={`${tid}-incl`} /></label>
        <div className="flex flex-col gap-0.5"><span className="text-[10px] uppercase tracking-wide text-muted-foreground">PVP</span>
          <span className="flex h-7 items-center justify-end text-sm font-semibold tabular-nums" data-testid={`${tid}-pvp`}>{fmtEUR(pvp)}</span></div>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onRemove} data-testid={`${tid}-remove`} aria-label="Quitar alojamiento"><Trash2 className="h-4 w-4" /></Button>
      </div>

      <div className="mt-2 pl-1">
        <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          Habitaciones <span className="font-semibold text-foreground" data-testid={`${tid}-rooms-sum`}>{(a.rooms || []).length} · {roomsPax} pax · {nights} noches</span>
          <span className="ml-2 inline-flex overflow-hidden rounded border text-[10px]" data-testid={`${tid}-price-mode`}>
            <button type="button" onClick={() => upd({ price_mode: "per_night" })} className={`px-1.5 py-0.5 ${!perStay ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`${tid}-price-mode-night`}>€/noche</button>
            <button type="button" onClick={() => upd({ price_mode: "per_stay" })} className={`px-1.5 py-0.5 ${perStay ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`${tid}-price-mode-stay`}>Total estancia</button>
          </span>
          {numTravelers && roomsPax !== numTravelers ? <span className="font-normal normal-case text-amber-600">(viaje de {numTravelers} pax)</span> : null}
        </div>
        {(a.rooms || []).length === 0 ? (
          <div className="mb-1 text-xs italic text-muted-foreground">Sin habitaciones — usando precio plano editable.</div>
        ) : (
          <div className="space-y-1">
            {a.rooms.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 border-l-2 border-muted pl-2 text-xs" data-testid={`${tid}-room-${r.id}`}>
                <select value={r.room_type} onChange={(e) => patchRoom(r.id, { room_type: e.target.value })} className="h-6 rounded border bg-background px-1 text-xs outline-none" data-testid={`${tid}-room-type-${r.id}`}>
                  {ROOM_TYPES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
                </select>
                <span className="text-muted-foreground">·</span>
                <input type="number" min="1" max="20" value={r.pax || 1} onChange={(e) => patchRoom(r.id, { pax: parseInt(e.target.value || "1", 10) })} className="h-6 w-12 rounded border bg-background px-1 text-right outline-none" title="Pax" data-testid={`${tid}-room-pax-${r.id}`} />
                <span className="text-muted-foreground">pax · {perStay ? "total estancia" : "€/noche"} c/IVA</span>
                <input type="number" min="0" step="0.01"
                  value={perStay ? round2((r.cost_incl || 0) * Math.max(1, nights)) : (r.cost_incl || 0)}
                  onChange={(e) => { const v = parseFloat(e.target.value || "0"); const perNight = perStay ? round2(v / Math.max(1, nights)) : v; patchRoom(r.id, { cost_incl: perNight, cost_excl: round2(perNight) }); }}
                  className="h-6 w-24 rounded border bg-background px-1 text-right font-semibold tabular-nums outline-none" data-testid={`${tid}-room-incl-${r.id}`} />
                {perStay && <span className="text-[10px] text-muted-foreground" data-testid={`${tid}-room-pernight-${r.id}`}>= {fmtEUR(r.cost_incl || 0)}/noche</span>}
                <input value={r.category || ""} onChange={(e) => patchRoom(r.id, { category: e.target.value })} placeholder="Categoría que ve el cliente (ej. Superior · vista mar)" title="Tipo de habitación contratado: se muestra al cliente en la web y el PDF" className="h-6 min-w-[200px] flex-1 rounded border bg-background px-1.5 text-[11px] outline-none focus:ring-1 focus:ring-ring" data-testid={`${tid}-room-category-${r.id}`} />
                <button onClick={() => removeRoom(r.id)} className="text-muted-foreground hover:text-destructive" title="Quitar habitación" data-testid={`${tid}-room-del-${r.id}`}><X className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
        <button onClick={addRoom} className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary" data-testid={`${tid}-add-room`}><Plus className="h-3 w-3" /> Añadir habitación</button>
      </div>

      <div className="mt-2">
        <div className="mb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Notas del alojamiento</div>
        <Input value={a.notes || ""} onChange={(e) => upd({ notes: e.target.value })} placeholder="Ej. habitación con vistas, late check-out, ubicación, peticiones especiales…" className="h-7 text-xs" data-testid={`${tid}-notes`} />
      </div>

      <CotSalesDescription
        name={a.name} plantillaId={a.plantilla_id} description={a.sales_description} images={a.sales_images}
        category="alojamiento" city={a.city}
        onChange={(patch) => onChange({ ...a, ...patch })}
        testid={`${tid}-sales`}
      />
    </>
  );

  if (!collapsible) return <div className="border-t first:border-t-0 p-3" data-testid={tid}>{body}</div>;

  return (
    <div className="rounded-md border bg-card" data-testid={tid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-accent/40" data-testid={`${tid}-toggle`}>
        <span className="flex min-w-0 items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
          {headerLabel ? <span className="shrink-0 rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">{headerLabel}</span> : <Bed className="h-4 w-4 shrink-0 text-primary" />}
          <span className="truncate text-sm font-medium">{a.name || "Alojamiento sin nombre"}</span>
          <span className="shrink-0 text-xs text-muted-foreground">· {nights} {nights === 1 ? "noche" : "noches"} · {(a.board || "bb").toUpperCase()}</span>
        </span>
        <span className="shrink-0 text-sm font-semibold tabular-nums">{fmtEUR(pvp)}</span>
      </button>
      {open && <div className="border-t p-3">{body}</div>}
    </div>
  );
}
